import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import jsPDF from 'jspdf';

import Modal from '../../components/Modal';
import ContentTabs from '../../components/ContentTabs';
import TabContent from './TabContent';
import TabSocial from './TabSocial';
import TabEmail from './TabEmail';

function Content() {
  const { id } = useParams();
  const navigate = useNavigate();
  const outputRef = useRef(null);

  const [contentData, setContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReading, setIsReading] = useState(false);
  const [showCodeView, setShowCodeView] = useState(false);
  const [codeViewContent, setCodeViewContent] = useState('');
  const [isCodeViewInitialized, setIsCodeViewInitialized] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [planName, setPlanName] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [postStatus, setPostStatus] = useState('publish'); 
  const [isPosting, setIsPosting] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);

  const contentTypeLabels = {
    blog: 'Blog Post',
    page: 'Web Page',
    linkedin: 'LinkedIn Post',
    x: 'X Post',
    instagram: 'Instagram Post',
    facebook: 'Facebook Post',
  };

  async function uploadImageToWordPress(site, imageUrl) {
    const imageRes = await fetch(imageUrl);
    const imageBlob = await imageRes.blob();
    const auth = btoa(`${site.username}:${site.app_password}`);
    const formData = new FormData();
    formData.append('file', imageBlob, 'featured.jpg');

    const uploadRes = await fetch(`${site.domain.replace(/\/$/, '')}/wp-json/wp/v2/media`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${auth}`,
      },
      body: formData,
    });

    if (!uploadRes.ok) {
      const errorData = await uploadRes.json();
      throw new Error(errorData.message || 'Image upload failed');
    }

    const uploadedImage = await uploadRes.json();
    return uploadedImage.id;
  }

  async function postToWordPress(site, post) {
    const url = `${site.domain.replace(/\/$/, '')}/wp-json/wp/v2/posts`;
    const auth = btoa(`${site.username}:${site.app_password}`);

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${auth}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(post),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || 'Failed to post to WordPress');
    }

    return await res.json();
  }

  async function getBlogId(site) {
    const res = await fetch('/api/shopify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'get-blogs',
        shop_name: site.shop_name,
        token: site.app_password,
      }),
    });

    if (!res.ok) {
      const err = await res.json();
      console.error('Failed to fetch blog:', err);
      throw new Error('Failed to fetch blogs');
    }

    const data = await res.json();
    return data.blogs?.[0]?.id;
  }

  async function postToShopify(site, contentData) {
    const blogId = await getBlogId(site);

    // const article = {
    //   title: contentData.title,
    //   body_html: `
    //     <img src="${contentData.featured_image}" alt="${contentData.title}" style="max-width: 100%; height: auto;" />
    //     ${contentData.content}
    //   `,
    //   tags: contentData.keywords?.join(', ') || '',
    //   author: site.shop_name,
    //   summary_html: contentData.excerpt,
    //   published: postStatus === 'publish',
    // };

    const imgHtml = contentData.featured_image
      ? `<img src="${contentData.featured_image}" alt="${contentData.title || 'Image'}" style="max-width: 100%; height: auto;" />\n`
      : '';

    const article = {
      title: contentData.title || 'Untitled',
      body_html: `${imgHtml}${contentData.content || ''}`,
      tags: Array.isArray(contentData.keywords)
        ? contentData.keywords.join(', ')
        : (contentData.keywords || ''),
      author: site.shop_name || 'Your Company',
      summary_html: contentData.excerpt || '',
      published: postStatus === 'publish',
    };

    const postRes = await fetch('/api/shopify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'post-article',
        shop_name: site.shop_name,
        token: site.app_password,
        blogId,
        article,
      }),
    });

    const postData = await postRes.json();

    if (!postRes.ok) {
      console.error('Shopify blog post error:', postData);
      throw new Error(postData?.error || 'Failed to post blog');
    }

    return postData;
  }

  useEffect(() => {
    const fetchWebsites = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) return;

      const { data, error } = await supabase
        .from('websites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading websites:', error);
      } else {
        setWebsites(data);
      }
    };

    if (showPostModal) fetchWebsites();
  }, [showPostModal]);

  useEffect(() => {
    const fetchContentAndPlan = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      if (!user) {
        console.warn('No user logged in');
        return;
      }

      const { data: account } = await supabase
        .from('accounts')
        .select('plan')
        .eq('id', user.id)
        .single();

      setPlanName(account?.plan === 'pro' ? 'Pro' : 'Free');

      const { data, error } = await supabase
        .from('generated_content')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching content:', error);
      } else {
        setContentData(data);

        if (data?.profile) {
          const { data: profileDetails } = await supabase
            .from('profiles')
            .select('name, description')
            .eq('id', data.profile)
            .single();

          setProfileData(profileDetails);
        }
      }

      setLoading(false);
    };

    fetchContentAndPlan();
  }, [id]);

  const isSocialPost = useMemo(() => {
    return ['linkedin', 'x', 'instagram', 'facebook'].includes(contentData?.content_type);
  }, [contentData?.content_type]);

  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('generated_content')
      .delete()
      .eq('id', id);
    setDeleting(false);

    if (error) {
      alert('Error deleting content.');
    } else {
      setShowDeleteModal(false);
      navigate('/content');
    }
  };

  if (loading) {
    return (
      <div className="centered-loading">
        <p>Loading content...</p>
      </div>
    );
  }

  if (!contentData) {
    return (
      <div className="centered-loading">
        <p>Content not found.</p>
      </div>
    );
  }

  const savedWordCount =
    contentData.word_count ||
    (contentData.content?.replace(/<[^>]+>/g, '').split(/\s+/).length || 0);

  return (
    <>
      <header className="header-content">
        <div className="section-title">
          <h1>{contentData.title}</h1>
        </div>
        <div className="breadcrumbs">
          <p>Content <i className="bi bi-chevron-right" /> View List <i className="bi bi-chevron-right" /> View</p>
        </div>
        <ContentTabs activeTab={activeTab} onTabChange={setActiveTab} />
      </header>

      <section className="scroll pt-100-d">
        <div className="contain">
          {activeTab === 'content' && (
            <TabContent
              outputRef={outputRef}
              contentData={contentData}
              profileData={profileData}
              isSocialPost={isSocialPost}
              contentTypeLabels={contentTypeLabels}
              contentWordCount={savedWordCount}
              codeViewContent={codeViewContent}
              showCodeView={showCodeView}
              isReading={isReading}
              isCodeViewInitialized={isCodeViewInitialized}
              handleCopy={(id) => {
                const el = document.getElementById(id);
                if (!el) return;
                if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
                  navigator.clipboard.writeText(el.value);
                } else {
                  navigator.clipboard.writeText(el.innerText || el.textContent);
                }
              }}
              toggleReadOutloud={() => {
                const synth = window.speechSynthesis;
                if (isReading) {
                  synth.cancel();
                  setIsReading(false);
                } else {
                  const utterance = new SpeechSynthesisUtterance();
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = contentData?.content || '';
                  const text = tempDiv.textContent || tempDiv.innerText || '';
                  utterance.text = text;
                  utterance.lang = 'en-US';
                  utterance.onend = () => setIsReading(false);
                  synth.cancel();
                  synth.speak(utterance);
                  setIsReading(true);
                }
              }}
              setShowCodeView={setShowCodeView}
              setIsCodeViewInitialized={setIsCodeViewInitialized}
              setCodeViewContent={setCodeViewContent}
              decodeHTMLEntities={(html) => {
                const txt = document.createElement('textarea');
                txt.innerHTML = html;
                return txt.value;
              }}
              handleImageDownload={async (url) => {
                try {
                  const response = await fetch(url, { mode: 'cors' });
                  const blob = await response.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.download = 'featured-image.jpg';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(blobUrl);
                } catch (error) {
                  console.error('Download failed:', error);
                  alert('Could not download image. Try right-click > Save as.');
                }
              }}
              handleExportPDF={() => {
                const doc = new jsPDF();
                const pageHeight = doc.internal.pageSize.height;
                const margin = 10;
                let y = margin;

                const addSection = (label, rawText, fontSize = 11) => {
                  if (!rawText) return;

                  doc.setFontSize(12);
                  doc.text(`${label}:`, margin, y);
                  y += 6;

                  doc.setFontSize(fontSize);
                  const lines = doc.splitTextToSize(rawText, 180);

                  lines.forEach((line) => {
                    if (y + 6 > pageHeight - margin) {
                      doc.addPage();
                      y = margin;
                    }
                    doc.text(line, margin, y);
                    y += 6;
                  });

                  y += 4;
                };

                doc.setFontSize(16);
                doc.text(contentData.title || 'Untitled', margin, y);
                y += 10;

                if (profileData) {
                  addSection('Profile Name', profileData.name);
                  addSection('Profile Description', profileData.description || 'No description provided.');
                }

                addSection('Slug', contentData.slug);
                addSection('Excerpt', contentData.excerpt);

                if (contentData.featured_image) {
                  const altText = (contentData.image_alt || contentData.title || 'Image').replace(/"/g, '&quot;');
                  const imageTag = `<img src="${contentData.featured_image}" alt="${altText}">`;
                  addSection('Featured Image (HTML)', imageTag);
                }

                if (contentData.ai_summary) {
                  const div = document.createElement('div');
                  div.innerHTML = contentData.ai_summary;
                  const summaryText = div.textContent || div.innerText || '';
                  addSection('AI Summary', summaryText);
                }

                if (contentData.content) {
                  const div = document.createElement('div');
                  div.innerHTML = contentData.content;
                  const contentText = div.textContent || div.innerText || '';
                  addSection('Content', contentText);
                }

                if (contentData.head && contentData.head.replace(/\s/g, '').length > 0 && !/^<meta>\s*<\/meta>$/.test(contentData.head)) {
                  addSection('Meta Tags', contentData.head);
                }

                doc.save(`${contentData.slug || 'content'}.pdf`);
              }}
              onPostToWebClick={() => setShowPostModal(true)}
              //setShowDeleteModal={planName === 'Pro' ? () => setShowDeleteModal(true) : undefined}
              navigate={navigate}
              planName={planName}
              setShowDeleteModal={() => setShowDeleteModal(true)}
            />
          )}

          {activeTab === 'social' && (
            <TabSocial
              topic={contentData.title || contentData.slug || ''}
              contentId={contentData.id}
              content={contentData.content}               
              includeText={contentData.ai_summary || ''}
            />
          )}

          {activeTab === 'email' && (
            <TabEmail
              topic={contentData.title || contentData.slug || ''}
              contentId={contentData.id}
              content={contentData.content}                
              includeText={contentData.ai_summary || ''}
            />
          )}
        </div>
      </section>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
        actions={[
          {
            label: deleting ? 'Deleting...' : 'Delete',
            className: 'btn-outlined btn-delete',
            onClick: handleDeleteConfirmed,
          },
          {
            label: 'Cancel',
            className: 'btn-outlined',
            onClick: () => setShowDeleteModal(false),
          },
        ]}
      >
        <p>Are you sure you want to delete this content? This action cannot be undone.</p>
      </Modal>

      {/* Post to Web Modal */}
      <Modal
        isOpen={showPostModal}
        onClose={() => {
          setShowPostModal(false);
          setPostSuccess(false); // Reset success state when modal is closed
        }}
        title="Post to Website"
        actions={[
          {
            label: postSuccess ? 'Posted!' : isPosting ? 'Posting...' : 'Post',
            className: `btn-outlined ${postSuccess ? 'success' : ''}`,
            disabled: isPosting || postSuccess,
            onClick: async () => {
              if (!selectedSite) {
                alert('Select a website to post to');
                return;
              }

              setIsPosting(true);

              try {
                if (selectedSite.cms_type === 'wordpress') {
                  let featuredImageId = null;

                  if (contentData.featured_image) {
                    featuredImageId = await uploadImageToWordPress(selectedSite, contentData.featured_image);
                  }

                  const post = {
                    title: contentData.title,
                    content: contentData.content,
                    excerpt: contentData.excerpt,
                    status: postStatus,
                    slug: contentData.slug,
                    ...(featuredImageId && { featured_media: featuredImageId }),
                  };

                  const res = await postToWordPress(selectedSite, post);
                  console.log('WordPress post response:', res);
                  setPostSuccess(true);
                  setTimeout(() => {
                    setShowPostModal(false);
                    setPostSuccess(false);
                  }, 1500);
                } else if (selectedSite.cms_type === 'shopify') {
                    const res = await postToShopify(selectedSite, contentData);
                    console.log('Shopify post response:', res);
                    setPostSuccess(true);
                    setTimeout(() => {
                      setShowPostModal(false);
                      setPostSuccess(false);
                  }, 1500);
                } else {
                  alert('Only WordPress posting is supported in this test.');
                }
              } catch (err) {
                console.error('Error posting:', err);
                alert(`Failed to post: ${err.message}`);
              } finally {
                setIsPosting(false); // Ensures this runs even if an error happens
              }
            }
          },
          {
            label: 'Cancel',
            className: 'btn-outlined',
            onClick: () => {
              setShowPostModal(false);
              setPostSuccess(false);
            },
          },
        ]}
      >
        <form className="create-item">
          <div className="note">
            <p>
              🟡 <strong>Posting Tip:</strong> <br/>
              To avoid being flagged by spam filters or security systems, we recommend posting to the same domain no more than <strong>5–10 times per week</strong>. Spread out your content to maintain trust and avoid triggering anti-spam mechanisms.
            </p>
          </div>
          <label htmlFor="site">Select Website:</label>
          <select
            id="site"
            name="site"
            value={selectedSite?.id || ''}
            onChange={(e) => {
              const selected = websites.find((w) => w.id === e.target.value);
              setSelectedSite(selected || null);
            }}
          >
            <option value="">-- Select a website --</option>
            {websites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.domain} ({site.cms_type})
              </option>
            ))}
          </select>

          <label htmlFor="status">Status:</label>
          <select
            id="status"
            name="status"
            value={postStatus}
            onChange={(e) => setPostStatus(e.target.value)}
          >
            <option value="publish">Publish</option>
            <option value="draft">Draft</option>
          </select>
        </form>
      </Modal>

    </>
  );
}

export default Content;